/**
 * 404 页面 — v6.97.62+3 P0 FIX 2026-08-03 老板报
 *
 * 真因: Next.js 16 默认 404 页面是英文 "This page could not be found"
 *       iPhone Safari 无痕模式 / iPad Safari / Mac Safari / Windows Chrome
 *       任何用户访问不存在的路由 (如 /dongniai/xxx) 或 iOS Safari 缓存
 *       错乱时 chunk 加载失败跳到 404 fallback, 都看到英文不友好页面。
 *
 * 修法: 自定义中文友好 404, 用原生 <script> 上报客户端错误 (不能用
 *       next/script 因为 build prerender 阶段 stringify 内部 React 元素
 *       报 "Event handlers cannot be passed to Client Component props",
 *       跟 7 个 SEO page (landing/compare/knowledge-graph/...) 同样错).
 *       跟其他 7 个 page 修法一致: 用原生 <script> 替代 next/script.
 *
 *       【v6.97.62+3 build 错修复】"清除缓存重试" 按钮 onClick 触发 build 错.
 *       修法: 用 useRef + useEffect 挂载后 addEventListener, JSX 里无 onClick
 *       inline function. 跟 app/error.tsx + app/global-error.tsx 同样修法.
 *
 *       【v6.97.62+3 client component】useRef 需要 'use client' directive.
 *       跟 error.tsx / global-error.tsx 都是 client component.
 */
'use client';

import { useEffect, useRef } from 'react';

export default function NotFound() {
  const resetButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const btn = resetButtonRef.current;
    if (btn) {
      const handler = () => {
        try {
          if (typeof window !== 'undefined' && 'caches' in window) {
            caches.keys().then((keys) =>
              Promise.all(keys.map((k) => caches.delete(k)))
            );
          }
        } catch {}
        if (typeof window !== 'undefined') {
          window.location.reload();
        }
      };
      btn.addEventListener('click', handler);
      return () => {
        btn.removeEventListener('click', handler);
      };
    }
  }, []);

  return (
    <div
      style={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        boxSizing: 'border-box',
        background: 'linear-gradient(180deg, #fafafa 0%, #f1f5f9 100%)',
      }}
    >
      {/* 客户端上报 — 用原生 <script> 而非 next/script (build prerender stringify 错) */}
      <script
        id="not-found-reporter"
        dangerouslySetInnerHTML={{
          __html: `(function(){
  try {
    if (window.__dongniaiNotFoundReported) return;
    window.__dongniaiNotFoundReported = true;
    var payload = {
      kind: 'not-found-404',
      message: '404: ' + (location.pathname || 'unknown'),
      url: location.href,
      userAgent: navigator.userAgent,
      referer: document.referrer || '',
      stack: '',
      digest: '',
      file: '',
      line: '',
      col: '',
    };
    fetch('/dongniai_client_error.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(function(){});
  } catch (e) {}
})();`,
        }}
      />
      <div
        style={{
          maxWidth: 480,
          width: '100%',
          background: '#fff',
          borderRadius: 16,
          padding: '32px 28px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
          textAlign: 'center',
          border: '1px solid #e2e8f0',
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            margin: '0 auto 16px',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            borderRadius: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 36,
            color: '#fff',
            fontWeight: 700,
            boxShadow: '0 8px 24px rgba(99, 102, 241, 0.25)',
          }}
        >
          404
        </div>
        <h1
          style={{
            fontSize: 22,
            fontWeight: 600,
            margin: '0 0 8px',
            color: '#0f172a',
            letterSpacing: '-0.01em',
          }}
        >
          页面没找到
        </h1>
        <p
          style={{
            fontSize: 14,
            color: '#64748b',
            margin: '0 0 24px',
            lineHeight: 1.6,
          }}
        >
          您访问的页面不存在或已被移动。
          <br />
          可能是浏览器缓存了旧的资源链接。
        </p>
        <div
          style={{
            display: 'flex',
            gap: 12,
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginBottom: 16,
          }}
        >
          <a
            href="/dongniai/"
            style={{
              padding: '10px 24px',
              fontSize: 14,
              fontWeight: 500,
              color: '#fff',
              background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
              border: 'none',
              borderRadius: 8,
              textDecoration: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
            }}
          >
            🏠 返回首页
          </a>
          <button
            ref={resetButtonRef}
            type="button"
            style={{
              padding: '10px 20px',
              fontSize: 14,
              fontWeight: 500,
              color: '#475569',
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            🔄 清除缓存重试
          </button>
        </div>
        <p
          style={{
            fontSize: 12,
            color: '#94a3b8',
            margin: '16px 0 0',
            lineHeight: 1.5,
          }}
        >
          如果问题持续，请截图发客服微信。
          <br />
          我们会立即排查。
        </p>
      </div>
    </div>
  );
}